/**
 * Shared audit-log viewer — powers both:
 *   • Super Admin incognito view (`GET /admin/users/{id}/audit-log[.csv]`)
 *   • Every user's own Settings → Audit log tab (`GET /me/audit-log[.csv]`)
 *
 * Props:
 *   jsonPath  — e.g., "/me/audit-log" or "/admin/users/abc/audit-log"
 *   csvPath   — e.g., "/me/audit-log.csv"
 *   subject   — display label ("Your activity", email, or user id)
 *   defaultDays  — initial window (7 | 30 | 90 | 180 | 365)
 *
 * Renders a window selector, 4 summary tiles, a scrollable event table,
 * a Download CSV button, and a Print button (opens a print-friendly
 * new window and calls `window.print()`).
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { Download, Printer, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_STYLES = {
    auth:    "bg-secondary/50 text-foreground",
    obondo:  "bg-primary/15 text-primary",
    trade:   "bg-success/15 text-success",
    order:   "bg-amber-500/15 text-amber-600",
    journal: "bg-purple-500/15 text-purple-600",
};

export default function AuditLogView({ jsonPath, csvPath, subject, defaultDays = 90 }) {
    const [days, setDays] = useState(defaultDays);
    const [loading, setLoading] = useState(false);
    const [payload, setPayload] = useState(null);
    const printAreaRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(jsonPath, { params: { days } });
            setPayload(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load audit log");
        } finally {
            setLoading(false);
        }
    }, [jsonPath, days]);

    useEffect(() => { load(); }, [load]);

    async function downloadCsv() {
        try {
            const res = await api.get(csvPath, { params: { days }, responseType: "blob" });
            const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
            const nameHint = (subject || "audit").replace(/[^a-z0-9]+/gi, "_");
            a.download = `${nameHint}-${stamp}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("CSV downloaded");
        } catch {
            toast.error("CSV export failed");
        }
    }

    function doPrint() {
        const el = printAreaRef.current;
        if (!el) return;
        const win = window.open("", "_blank", "width=900,height=700");
        if (!win) {
            toast.error("Popup blocked — allow popups to print, or use CSV download instead.");
            return;
        }
        const styles = `
            body{font-family:system-ui,sans-serif;padding:24px;color:#111}
            h1{font-size:16px;margin:0 0 4px}
            h2{font-size:12px;margin:12px 0 4px;color:#555;text-transform:uppercase;letter-spacing:.06em}
            table{width:100%;border-collapse:collapse;font-size:11px;margin-top:8px}
            th,td{border:1px solid #ccc;padding:4px 6px;text-align:left;vertical-align:top}
            th{background:#f4f4f4;text-transform:uppercase;font-size:10px}
            .mono{font-family:ui-monospace,monospace}
            .muted{color:#666}
        `;
        const s = payload?.summary || {};
        win.document.write(`
            <html><head><title>Audit log · ${subject || ""}</title>
            <style>${styles}</style></head>
            <body>
              <h1>Audit log — ${subject || ""}</h1>
              <div class="muted mono">Generated ${payload?.generated_at} · Window: last ${payload?.days} days</div>
              <h2>Summary</h2>
              <ul>
                <li>Total events: <b>${s.total_events ?? 0}</b></li>
                <li>Logins: ${s.logins ?? 0} · Logouts: ${s.logouts ?? 0}</li>
                <li>Manual trades opened: ${s.manual_opens ?? 0} · closed: ${s.manual_closes ?? 0}</li>
                <li>Obondo AI trades opened: ${s.obondo_opens ?? 0} · closed: ${s.obondo_closes ?? 0}</li>
                <li>Orders: ${s.orders ?? 0} · Journal entries: ${s.journal ?? 0}</li>
                <li>Unique IPs: ${(s.unique_ips || []).join(", ") || "—"}</li>
              </ul>
              ${el.innerHTML}
              <script>window.onload=()=>{window.print();}</script>
            </body></html>
        `);
        win.document.close();
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center py-10 text-muted-foreground" data-testid="audit-loading">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading audit log…
            </div>
        );
    }

    const events = payload?.events || [];
    const s = payload?.summary || {};
    const tradesTotal = (s.manual_opens ?? 0) + (s.manual_closes ?? 0)
                      + (s.obondo_opens ?? 0) + (s.obondo_closes ?? 0);

    return (
        <div data-testid="audit-log-view">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                    <label className="text-[10px] uppercase text-muted-foreground tracking-wider">Window</label>
                    <select
                        data-testid="audit-days-select"
                        value={days}
                        onChange={(e) => setDays(Number(e.target.value))}
                        className="bg-background border border-border rounded px-2 py-1 text-xs"
                    >
                        <option value={7}>Last 7 days</option>
                        <option value={30}>Last 30 days</option>
                        <option value={90}>Last 90 days</option>
                        <option value={180}>Last 180 days</option>
                        <option value={365}>Last 365 days</option>
                    </select>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        data-testid="audit-download-csv"
                        onClick={downloadCsv}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground text-xs font-semibold transition"
                    >
                        <Download className="w-3 h-3" /> Download CSV
                    </button>
                    <button
                        type="button"
                        data-testid="audit-print"
                        onClick={doPrint}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-border hover:bg-secondary text-xs font-semibold transition"
                    >
                        <Printer className="w-3 h-3" /> Print
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3 text-xs">
                <StatCell label="Events"       value={s.total_events ?? 0} testid="audit-stat-total" />
                <StatCell label="Logins"       value={s.logins ?? 0}       testid="audit-stat-logins" />
                <StatCell label="Trades"       value={tradesTotal}         testid="audit-stat-trades" />
                <StatCell label="Journal"      value={s.journal ?? 0}      testid="audit-stat-journal" />
                <StatCell label="Unique IPs"   value={(s.unique_ips || []).length} testid="audit-stat-ips" />
            </div>

            <div ref={printAreaRef} className="border border-border rounded overflow-hidden max-h-[420px] overflow-y-auto">
                <table className="w-full text-xs">
                    <thead className="bg-secondary/30 text-muted-foreground sticky top-0 z-10">
                        <tr>
                            <th className="text-left px-3 py-2">Timestamp</th>
                            <th className="text-left px-3 py-2">Category</th>
                            <th className="text-left px-3 py-2">Action</th>
                            <th className="text-left px-3 py-2">Detail</th>
                            <th className="text-left px-3 py-2">IP</th>
                            <th className="text-left px-3 py-2">User agent</th>
                        </tr>
                    </thead>
                    <tbody>
                        {events.map((e, i) => (
                            <tr key={e.id || e.ts || `audit-${i}`} className="border-t border-border" data-testid={`audit-row-${i}`}>
                                <td className="px-3 py-2 mono text-[10px] whitespace-nowrap">
                                    {e.ts && new Date(e.ts).toLocaleString("en-KE")}
                                </td>
                                <td className="px-3 py-2">
                                    <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] uppercase font-bold ${
                                        CATEGORY_STYLES[e.category] || "bg-secondary/50 text-foreground"
                                    }`}>
                                        {e.category}
                                    </span>
                                </td>
                                <td className="px-3 py-2 uppercase font-semibold text-[10px]">{e.action}</td>
                                <td className="px-3 py-2 mono text-[10px]">{e.detail}</td>
                                <td className="px-3 py-2 mono text-[10px]">{e.ip || "—"}</td>
                                <td className="px-3 py-2 text-[10px] text-muted-foreground truncate max-w-[220px]" title={e.user_agent || ""}>
                                    {e.user_agent || "—"}
                                </td>
                            </tr>
                        ))}
                        {events.length === 0 && (
                            <tr>
                                <td colSpan={6} className="p-6 text-center text-muted-foreground text-xs">
                                    No events in the last {days} days.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function StatCell({ label, value, testid }) {
    return (
        <div className="border border-border rounded p-2 bg-card">
            <div className="text-[10px] text-muted-foreground uppercase">{label}</div>
            <div className="mono text-lg" data-testid={testid}>{value}</div>
        </div>
    );
}
